<template>
  <div>
    <div class="row">
      <div class="col-sm-2">
        <OverviewBox title="Production Panel" avatarImage="/glass/img/production-overview-station.png" :avatarCircleMode="false" :loading="pageDataLoading">
          <h4>Production Panel</h4>
          
          <OverviewBoxList>
            <OverviewBoxListItem title="Barcode">
              {{panel.barcode}}
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Status">
              <LabelPanelStatusBadge :status="panel.status" />
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Panel Index">
              {{panel.panelIndex + 1}} / {{panel.orderItem.quantity}}
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Size">
              {{panel.panelHeight}} x {{panel.panelWidth}}
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Order Item">
              {{panel.orderItem.description}}
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Item Info">
              {{panel.orderItem.generatedDescription | formatHyphenEmptyValue}}
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Toughening Batch" v-if="tougheningBatch">
              <LinkTougheningBatch :id="tougheningBatch.id" :name="`#${tougheningBatch.sequenceNumber}`" />
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Order">
              <LinkOrder :id="panel.orderId" :name="`#${panel.order.sequenceNumber}`" />
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Optimiser Sheet" v-if="optimiserSheet">
              <LinkOptimiserSheet :id="optimiserSheet.id" :name="`Sheet ${optimiserSheet.id}`" />
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Bin" v-if="panel.inventoryBin">
              {{panel.inventoryBin.code}}
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Location" v-if="panel.inventoryBin && panel.inventoryBin.accountLocation">
              {{panel.inventoryBin.accountLocation.name}}
            </OverviewBoxListItem>

            <OverviewBoxListItem title="Created">{{panel.createdAt | formatDateTime | formatHyphenEmptyValue}}</OverviewBoxListItem>
            <OverviewBoxListItem title="Completed">{{panel.dateCompleted | formatDateTime | formatHyphenEmptyValue}}</OverviewBoxListItem>
          </OverviewBoxList>
        </OverviewBox>

      </div>

      <div class="col-sm-10">
        <TabbedBox ref="tabBox" :loading="pageDataLoading">
          <template v-slot:tablist>
            <Tab :first="true" tabid="tab-panel-summary">
              <i class="far fa-clipboard-list-check"></i> Panel Details
            </Tab>
            <Tab tabid="tab-panel-costs">
              <i class="far fa-usd-circle"></i> Production Costs
            </Tab>
            <Tab tabid="tab-panel-recut" v-if="isRecutStatus">
              <i class="far fa-sync-alt"></i> Recut
            </Tab>
            <Tab tabid="tab-panel-documents">
              <i class="far fa-book"></i> Documents
            </Tab>
            <Tab tabid="tab-panel-activitylog">
              <i class="far fa-history"></i> Activity Log
            </Tab>
          </template>
          <template v-slot:tabcontent>
            <TabPage :first="true" tabid="tab-panel-summary">
              
              <DataPageArea title="Details">
                <template slot="dataArea">
                  <DataItem title="Barcode">{{panel.barcode}}</DataItem>
                  <DataItem title="Size">{{panel.panelHeight}} x {{panel.panelWidth}}</DataItem>
                  <DataItem title="Stock" v-if="panel.orderItem && panel.orderItem.stock">
                    <LinkStock :id="panel.orderItem.stock.id" :name="`${panel.orderItem.stock.code} - ${panel.orderItem.stock.name}`" />
                  </DataItem>
                  <DataItem title="Cancelled Reason" v-if="isCancelled">{{panel.cancelledReason}}</DataItem>
                </template>
              </DataPageArea>

              <div class="row mt-2">
                <div class="col-12">
                  <ContentTitle>Production Tasks</ContentTitle>
                  <StaticDataTable>
                    <template v-slot:header>
                      <tr>
                        <th>Stage</th>
                        <th>Status</th>
                        <th>Due Date</th>
                        <th>Started Date</th>
                        <th>Completed Date</th>
                        <th>Station Overrides</th>
                        <th>Location</th>
                      </tr>
                    </template>
                    <template v-slot:body>
                      <tr v-for="stageTask in panel.productionPanelStageTasks" :key="stageTask.id">
                        <td>
                          <LinkProductionStage :id="stageTask.productionStageId" :name="stageTask.productionStage.name" />
                        </td>
                        <td>
                          <LabelStageTaskStatusBadge :status="stageTask.status" />
                        </td>
                        <td>
                          {{stageTask.dueDate | formatDateTime}}
                        </td>
                        <td>
                          {{stageTask.startedDate | formatDateTime | formatHyphenEmptyValue}}
                        </td>
                        <td>
                          {{stageTask.completionDate | formatDateTime | formatHyphenEmptyValue}}
                        </td>
                        <td>
                          {{stageTaskStationOverrideDisplay(stageTask)}}
                        </td>
                        <td>
                          <AccountLocationDisplay :id="stageTask.accountLocationId" />
                        </td>
                      </tr>
                    </template>
                    <template v-slot:empty v-if="panel.productionPanelStageTasks.length == 0">No tasks found</template>
                  </StaticDataTable>
                </div>
              </div>
            </TabPage>

            <TabPage tabid="tab-panel-costs">
              <div class="row">
                <div class="col-12">
                  <ContentTitle>Processing Costs</ContentTitle>
                  <StaticDataTable>
                    <template v-slot:header>
                      <tr>
                        <th>Stock Cost</th>
                        <th>Production Cost</th>
                        <th>Despatch Cost</th>
                        <th>Total</th>
                      </tr>
                    </template>
                    <template v-slot:body>
                      <tr>
                        <td>{{panel.processingStockValue | formatCurrency}}</td>
                        <td>{{panel.processingProductionValue | formatCurrency}}</td>
                        <td>{{panel.processingDespatchValue | formatCurrency}}</td>
                        <td class="font-weight-bold">{{panel.processingStockValue + panel.processingProductionValue + panel.processingDespatchValue | formatCurrency}}</td>
                      </tr>
                    </template>
                  </StaticDataTable>
                </div>
              </div>
            </TabPage>

            <TabPage tabid="tab-panel-recut" v-if="isRecutStatus">

              <DataPageArea title="Panel Recut" class="mt-2">
                <template slot="dataArea">
                  <DataItem title="Reporting User">{{panel.recutReportingUser}}</DataItem>
                  <DataItem title="Date Recut">{{panel.recutDate | formatDateTime}}</DataItem>
                  <DataItem title="Labels">
                    <LabelTags :labelList="panel.labelList" />
                  </DataItem>
                  <DataItem title="Notes">{{panel.recutNotes}}</DataItem>
                </template>
              </DataPageArea>
            </TabPage>

            <TabPage tabid="tab-panel-documents">
              <div class="row">
                <div class="col-lg-12">
                  <DocumentList
                    title="Documents"
                    :linkType="LinkTypes.productionPanel"
                    :linkId="panel.id"
                    class="mt-2" />
                </div>
              </div>
            </TabPage>

            <TabPage tabid="tab-panel-activitylog">
              <div class="row">
                <div class="col-lg-12">
                  <ActivityLogStream ref="activityStream" :url="activityStreamURL" :linkType="LinkTypes.productionPanel" :linkId="panel.id">
                    <template v-slot:title><h3>Recent Activity</h3></template>
                  </ActivityLogStream>
                </div>
              </div>
            </TabPage>
          </template>
        </TabbedBox>
      </div>
    </div>
  </div>
</template>

<script>
import DocumentList from '@/components/documentList.vue'
import AccountLocationDisplay from '@/modules/admin/components/accountLocationDisplay'
import LabelPanelStatusBadge from '@/modules/production/components/labelPanelStatusBadge'
import LabelStageTaskStatusBadge from '@/modules/production/components/labelStageTaskStatusBadge'

import { LinkTypes, ProductionPanelStatusTypes, OptimiserSheetStatusTypes } from '@/store/types'

export default {
  name: 'ProductionPanel',
  components: {
    AccountLocationDisplay,
    LabelPanelStatusBadge,
    LabelStageTaskStatusBadge,
    DocumentList
  },
  data() {
    return {
      panel: {
        orderItem: {},
        order: {},
        productionPanelStageTasks: []
      }
    }
  },
  computed: {
    activityStreamURL() {
      if(!this.$route.params.id)
        return null
      return `/production/panels/${this.$route.params.id}/activitystream`
    },
    tougheningBatch() {
      if(this.panel.tougheningBatchPanels && this.panel.tougheningBatchPanels.length > 0) {
        return this.panel.tougheningBatchPanels[0].tougheningBatch
      }
      return null
    },
    optimiserSheet() {
      if(this.panel.optimiserSheetAllocations) {
        var validAlloc = this.panel.optimiserSheetAllocations.find(alloc => alloc.optimiserSheet && alloc.optimiserSheet.status != OptimiserSheetStatusTypes.cancelled)
        if(validAlloc)
          return validAlloc.optimiserSheet
      }
      return null
    },
    isCancelled() {
      return this.panel.status == ProductionPanelStatusTypes.cancelled
    },
    isRecutStatus() {
      return this.panel.status == ProductionPanelStatusTypes.recut
    }
  },
  mounted() {
    this.requestData()
  },
  created() {
    this.LinkTypes = LinkTypes
  },
  methods: {
    requestData() {
      this.pageLoadData(this.$apiService.productionService.getProductionPanel(this.$route.params.id))
      .then(response => {
        this.panel = response.data
      })
      .catch(err => this.handlePageLoadError(err))
    },
    stageTaskStationOverrideDisplay(stageTask) {
      if(!stageTask.productionStations || stageTask.productionStations.length == 0)
        return '-'

      return stageTask.productionStations.map(productionStation => productionStation.name).join(', ')
    }
  }
}
</script>